import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { gridSpacing } from 'store/constant';
import {
  fetchCarePlan,
  setCarePlanAsync,
  setSelectedSection
} from '../../../slices/NdisSlices/carePlanInfo/CarePlanInfoSlice';
import {
  CarePlan,
  DirectedSupportPlan,
  SectionProps,
  HealthInfoGroup,
  RegularAppointment,
  ContingencyAndDisasterPlan,
  Medication
} from '../../../types/ndisSection/carePlanInfoTypes';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './ValidationSchema';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import {
  FaUser,
  FaClinicMedical,
  FaWheelchair,
  FaUtensils,
  FaExclamationTriangle,
  FaStar,
  FaHospital,
  FaCalendarAlt,
  FaExclamationCircle,
  FaHandsHelping,
  FaPills,
  FaListAlt,
  FaTimes,
  FaBars
} from 'react-icons/fa';
import { useClientContext } from '../ProfileMainPage';
import { MobileNavButton } from './Components';
import PersonalInfoSection from './sections/PersonalInfoSection';
import HealthInfoSection from './sections/HealthInfoSection';
import AbilitiesSection from './sections/AbilitiesSection';
import MealPreferencesSection from './sections/MealPreferencesSection';
import EmergencyContactsSection from './sections/EmergencyContactsSection';
import GoalsSection from './sections/GoalsSection';
import MedicalProfessionalsSection from './sections/MedicalProfessionalsSection';
import RegularAppointmentsSection from './sections/RegularAppointmentsSection';
import ContingencyPlanSection from './sections/ContingencyPlanSection';
import DirectedSupportPlanSection from './sections/DirectedSupportPlanSection';
import MedicationSection from './sections/MedicationSection';
import { Dispatch, SetStateAction } from 'react';

// Extend SectionProps locally if carePlanInfoTypes.ts cannot be modified
interface ExtendedSectionProps extends SectionProps {
  regularAppointments: RegularAppointment[];
  setRegularAppointments: Dispatch<SetStateAction<RegularAppointment[]>>;
}

interface RootState {
  carePlanInfo: {
    carePlan: CarePlan | null;
    selectedSection: string;
    loading: boolean;
    error: string | null;
  };
  homeTilesServices?: {
    selectedProjectId: string;
  };
  serviceSelected: {
    selectedServiceTitle: string;
  };
}

// Define type for sections array
interface Section {
  name: string;
  icon: JSX.Element;
  color: string;
  description: string;
}

const CarePlanComponent: React.FC = () => {
  const { selectedClient } = useClientContext();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    carePlan,
    selectedSection,
    loading,
    error: errorMessage
  } = useSelector((state: RootState) => state.carePlanInfo);
  const selectedProjectId = useSelector(
    (state: RootState) => state.homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected?.selectedServiceTitle || ''
  );
  const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');
  const location = useLocation();
  const componentParentId = location.pathname;

  // State for edit mode
  const [editMode, setEditMode] = useState(false);

  // State for array fields
  const [medicalHistory, setMedicalHistory] = useState<string[]>(
    carePlan?.HealthInformation?.MedicalHistory ?? []
  );
  const [healthInfoGroups, setHealthInfoGroups] = useState<HealthInfoGroup[]>(
    carePlan?.HealthInformation?.Groups ?? []
  );
  const [dislikes, setDislikes] = useState<string[]>(carePlan?.MealPreferences?.Dislikes ?? ['']);
  const [shortTermGoals, setShortTermGoals] = useState<string[]>(
    carePlan?.Goals?.ShortTerm ?? ['']
  );
  const [longTermGoals, setLongTermGoals] = useState<string[]>(carePlan?.Goals?.LongTerm ?? ['']);
  const [directedSupportPlans, setDirectedSupportPlans] = useState<DirectedSupportPlan[]>(
    carePlan?.DirectedSupportPlan ?? [
      {
        CareNeed: '',
        FrequencyOfServices: '',
        ActionStrategies: '',
        VisitTimeDuration: '',
        ResponsiblePerson: '',
        ByWhen: ''
      }
    ]
  );
  const [regularAppointments, setRegularAppointments] = useState<RegularAppointment[]>(
    carePlan && Array.isArray(carePlan.RegularAppointments)
      ? carePlan.RegularAppointments.map((appt) => ({
          AppointmentType: appt.AppointmentType ?? '',
          Frequency: appt.Frequency ?? '',
          ContactDetails: appt.ContactDetails ?? '',
          Physio: appt.Physio ?? ''
        }))
      : [
          {
            AppointmentType: '',
            Frequency: '',
            ContactDetails: '',
            Physio: ''
          }
        ]
  );

  // Normalize Medication to always be an array
  const normalizeMedication = (
    medication: Medication | Medication[] | string | undefined
  ): Medication[] => {
    if (!medication) {
      return [{ Details: '' }];
    }
    if (typeof medication === 'string') {
      return [{ Details: medication }];
    }
    if (!Array.isArray(medication)) {
      return [medication];
    }
    return medication.length > 0 ? medication : [{ Details: '' }];
  };

  // Normalize MedicalProfessionals to always be an array
  const normalizeMedicalProfessionals = (
    professionals: CarePlan['MedicalProfessionals'] | undefined
  ): CarePlan['MedicalProfessionals'] => {
    if (!professionals) {
      return [{ GP: '', Specialist: { SpecialistCare: [''] }, AlliedHealth: '' }];
    }
    if (!Array.isArray(professionals)) {
      return [professionals];
    }
    return professionals.length > 0
      ? professionals.map((prof) => ({
          GP: prof.GP ?? '',
          Specialist: { SpecialistCare: prof.Specialist?.SpecialistCare ?? [''] },
          AlliedHealth: prof.AlliedHealth ?? ''
        }))
      : [{ GP: '', Specialist: { SpecialistCare: [''] }, AlliedHealth: '' }];
  };

  // Initialize React Hook Form
  const methods = useForm<CarePlan>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...carePlan,
      Name: '',
      PreferredName: '',
      Address: '',
      Phone: '',
      DateOfBirth: '',
      MaritalStatus: '',
      Nationality: '',
      LivingArrangement: '',
      NDISNumber: '',
      PlanReviewDate: '',
      HealthInformation: {
        MedicalHistory: medicalHistory,
        Groups: healthInfoGroups
      },
      AbilitiesAndSupportNeeds: {
        Mobility: '',
        Communication: '',
        SelfCare: '',
        SocialInteraction: '',
        SupportNeeds: '',
        Continence: '',
        Hearing: '',
        Vision: '',
        Cognition: '',
        SwallowingDifficulty: ''
      },
      MealPreferences: {
        Likes: '',
        Dislikes: dislikes,
        DietaryRequirements: '',
        PreferredDiet: ''
      },
      Goals: { ShortTerm: shortTermGoals, LongTerm: longTermGoals },
      MedicalProfessionals: [
        {
          GP: '',
          Specialist: { SpecialistCare: [''] },
          AlliedHealth: ''
        }
      ],
      DirectedSupportPlan: directedSupportPlans,
      EmergencyContact: {
        Husband: { Name: '', Phone: '' },
        Son: { Name: '', Phone: '' }
      },
      ContingencyAndDisasterPlan: [
        {
          Scenario: '',
          RiskLevel: '',
          ImmediateAction: '',
          OngoingAction: ''
        }
      ],
      Medication: [{ Details: '' }],
      RegularAppointments: regularAppointments,
      Pharmacy: {
        Name: '',
        Location: '',
        Phone: ''
      },
      ResponsePlanIfClientNotHome: ''
    }
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue
  } = methods;

  // Sync state with form
  useEffect(() => {
    setValue('HealthInformation.MedicalHistory', medicalHistory);
  }, [medicalHistory, setValue]);

  useEffect(() => {
    setValue('HealthInformation.Groups', healthInfoGroups);
  }, [healthInfoGroups, setValue]);

  useEffect(() => {
    setValue('MealPreferences.Dislikes', dislikes);
  }, [dislikes, setValue]);

  useEffect(() => {
    setValue('Goals.ShortTerm', shortTermGoals);
  }, [shortTermGoals, setValue]);

  useEffect(() => {
    setValue('Goals.LongTerm', longTermGoals);
  }, [longTermGoals, setValue]);

  useEffect(() => {
    setValue('DirectedSupportPlan', directedSupportPlans);
  }, [directedSupportPlans, setValue]);

  useEffect(() => {
    setValue('RegularAppointments', regularAppointments);
  }, [regularAppointments, setValue]);

  // Fetch care plan data
  useEffect(() => {
    if (selectedClient) {
      const payload = {
        user_whoRequested_id: parsedUserDetails.name,
        group_id: selectedProjectId || '',
        service_name_id: selectedServiceTitle.replace(/ /g, '_') || '',
        component_parent_id: componentParentId,
        nested_component_id: 'profile',
        client_name: selectedClient.label || ''
      };
      dispatch(fetchCarePlan(payload));
    }
  }, [
    selectedClient,
    dispatch,
    parsedUserDetails.name,
    selectedProjectId,
    selectedServiceTitle,
    componentParentId
  ]);

  // Normalize ContingencyAndDisasterPlan to always be an array
  const normalizeContingencyPlan = (
    plan: ContingencyAndDisasterPlan | ContingencyAndDisasterPlan[] | undefined
  ): ContingencyAndDisasterPlan[] => {
    if (!plan) {
      return [{ Scenario: '', RiskLevel: '', ImmediateAction: '', OngoingAction: '' }];
    }
    if (!Array.isArray(plan)) {
      return [plan];
    }
    return plan.length > 0
      ? plan
      : [{ Scenario: '', RiskLevel: '', ImmediateAction: '', OngoingAction: '' }];
  };

  // Reset form and state when carePlan changes
  useEffect(() => {
    if (carePlan) {
      const normalizedContingencyPlan = normalizeContingencyPlan(
        carePlan.ContingencyAndDisasterPlan
      );
      const normalizedMedication = normalizeMedication(carePlan.Medication);
      const normalizedMedicalProfessionals = normalizeMedicalProfessionals(
        carePlan.MedicalProfessionals
      );
      reset({
        ...carePlan,
        ContingencyAndDisasterPlan: normalizedContingencyPlan,
        Medication: normalizedMedication,
        MedicalProfessionals: normalizedMedicalProfessionals
      });
      setMedicalHistory(carePlan.HealthInformation?.MedicalHistory ?? []);
      setHealthInfoGroups(
        carePlan.HealthInformation?.Groups ?? [
          {
            Allergies: 'all1',
            CurrentMedications: 'cm1',
            HealthConditions: 'hc1',
            PrimaryDiagnosis: 'pd1',
            FallsRisk: 'fr1'
          },
          {
            Allergies: '',
            CurrentMedications: '',
            HealthConditions: '',
            PrimaryDiagnosis: '',
            FallsRisk: ''
          }
        ]
      );
      setDislikes(carePlan.MealPreferences?.Dislikes ?? ['']);
      setShortTermGoals(carePlan.Goals?.ShortTerm ?? ['']);
      setLongTermGoals(carePlan.Goals?.LongTerm ?? ['']);
      setDirectedSupportPlans(
        carePlan.DirectedSupportPlan ?? [
          {
            CareNeed: '',
            FrequencyOfServices: '',
            ActionStrategies: '',
            VisitTimeDuration: '',
            ResponsiblePerson: '',
            ByWhen: ''
          }
        ]
      );
      setRegularAppointments(
        carePlan && Array.isArray(carePlan.RegularAppointments)
          ? carePlan.RegularAppointments.map((appt) => ({
              AppointmentType: appt.AppointmentType ?? '',
              Frequency: appt.Frequency ?? '',
              ContactDetails: appt.ContactDetails ?? '',
              Physio: appt.Physio ?? ''
            }))
          : [
              {
                AppointmentType: '',
                Frequency: '',
                ContactDetails: '',
                Physio: ''
              }
            ]
      );
    }
  }, [carePlan, reset]);

  const handleSectionClick = (section: string) => {
    dispatch(setSelectedSection(section));
    if (isMobile) {
      setDrawerOpen(false);
    }
    setEditMode(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (!editMode && carePlan) {
      const normalizedContingencyPlan = normalizeContingencyPlan(
        carePlan.ContingencyAndDisasterPlan
      );
      const normalizedMedication = normalizeMedication(carePlan.Medication);
      const normalizedMedicalProfessionals = normalizeMedicalProfessionals(
        carePlan.MedicalProfessionals
      );
      reset({
        ...carePlan,
        ContingencyAndDisasterPlan: normalizedContingencyPlan,
        Medication: normalizedMedication,
        MedicalProfessionals: normalizedMedicalProfessionals
      });
      setMedicalHistory(carePlan.HealthInformation?.MedicalHistory ?? ['add1', 'add12']);
      setHealthInfoGroups(
        carePlan.HealthInformation?.Groups ?? [
          {
            Allergies: 'all1',
            CurrentMedications: 'cm1',
            HealthConditions: 'hc1',
            PrimaryDiagnosis: 'pd1',
            FallsRisk: 'fr1'
          },
          {
            Allergies: '',
            CurrentMedications: '',
            HealthConditions: '',
            PrimaryDiagnosis: '',
            FallsRisk: ''
          }
        ]
      );
      setDislikes(carePlan.MealPreferences?.Dislikes ?? ['']);
      setShortTermGoals(carePlan.Goals?.ShortTerm ?? ['']);
      setLongTermGoals(carePlan.Goals?.LongTerm ?? ['']);
      setDirectedSupportPlans(
        carePlan.DirectedSupportPlan ?? [
          {
            CareNeed: '',
            FrequencyOfServices: '',
            ActionStrategies: '',
            VisitTimeDuration: '',
            ResponsiblePerson: '',
            ByWhen: ''
          }
        ]
      );
      setRegularAppointments(
        carePlan && Array.isArray(carePlan.RegularAppointments)
          ? carePlan.RegularAppointments.map((appt) => ({
              AppointmentType: appt.AppointmentType ?? '',
              Frequency: appt.Frequency ?? '',
              ContactDetails: appt.ContactDetails ?? '',
              Physio: appt.Physio ?? ''
            }))
          : [
              {
                AppointmentType: '',
                Frequency: '',
                ContactDetails: '',
                Physio: ''
              }
            ]
      );
    }
  };

  console.log('healthInfoGroups', healthInfoGroups);

  const onSubmit = (data: CarePlan, e?: React.BaseSyntheticEvent) => {
    console.log('onSubmit care plan', data, e);
    console.log('Form errors:', errors);
    try {
      const transformedCarePlan: CarePlan = {
        ...data
        // HealthInformation: {
        //   MedicalHistory: medicalHistory,
        //   Groups: healthInfoGroups.map((group, index) => ({
        //     Allergies: data.HealthInformation?.Groups?.[index]?.Allergies ?? group.Allergies ?? '',
        //     CurrentMedications:
        //       data.HealthInformation?.Groups?.[index]?.CurrentMedications ??
        //       group.CurrentMedications ??
        //       '',
        //     HealthConditions:
        //       data.HealthInformation?.Groups?.[index]?.HealthConditions ??
        //       group.HealthConditions ??
        //       '',
        //     PrimaryDiagnosis:
        //       data.HealthInformation?.Groups?.[index]?.PrimaryDiagnosis ??
        //       group.PrimaryDiagnosis ??
        //       '',
        //     FallsRisk: data.HealthInformation?.Groups?.[index]?.FallsRisk ?? group.FallsRisk ?? ''
        //   }))
        // },
        // MealPreferences: {
        //   ...data.MealPreferences,
        //   Dislikes: dislikes
        // },
        // Goals: {
        //   ShortTerm: shortTermGoals,
        //   LongTerm: longTermGoals
        // },
        // MedicalProfessionals: data.MedicalProfessionals?.map((prof, index) => ({
        //   GP: prof.GP ?? '',
        //   Specialist: {
        //     SpecialistCare: prof.Specialist?.SpecialistCare ?? ['']
        //   },
        //   AlliedHealth: prof.AlliedHealth ?? ''
        // })),
        // DirectedSupportPlan: directedSupportPlans.map((plan, index) => ({
        //   ...plan,
        //   ...(data.DirectedSupportPlan?.[index] || {})
        // })),
        // EmergencyContact: data.EmergencyContact ?? {
        //   Husband: { Name: '', Phone: '' },
        //   Son: { Name: '', Phone: '' }
        // },
        // ContingencyAndDisasterPlan: normalizeContingencyPlan(data.ContingencyAndDisasterPlan),
        // Medication: normalizeMedication(data.Medication),
        // RegularAppointments: regularAppointments.map((appointment, index) => ({
        //   AppointmentType:
        //     data.RegularAppointments?.[index]?.AppointmentType ?? appointment.AppointmentType ?? '',
        //   Frequency: data.RegularAppointments?.[index]?.Frequency ?? appointment.Frequency ?? '',
        //   ContactDetails:
        //     data.RegularAppointments?.[index]?.ContactDetails ?? appointment.ContactDetails ?? '',
        //   Physio: data.RegularAppointments?.[index]?.Physio ?? appointment.Physio ?? ''
        // })),
        // Pharmacy: data.Pharmacy ?? {
        //   Name: '',
        //   Location: '',
        //   Phone: ''
        // },
        // ResponsePlanIfClientNotHome: data.ResponsePlanIfClientNotHome ?? ''
      };
      console.log('transformedCarePlan', transformedCarePlan);
      dispatch(
        setCarePlanAsync({
          carePlan: transformedCarePlan,
          user_whoRequested_id: parsedUserDetails.name,
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle.replace(/ /g, '_') || '',
          nested_component_id: 'profile',
          client_name: selectedClient ? selectedClient.label : ''
        })
      );
      setEditMode(false);
    } catch (error) {
      console.error('Error in onSubmit:', error);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (carePlan) {
      const normalizedContingencyPlan = normalizeContingencyPlan(
        carePlan.ContingencyAndDisasterPlan
      );
      const normalizedMedication = normalizeMedication(carePlan.Medication);
      const normalizedMedicalProfessionals = normalizeMedicalProfessionals(
        carePlan.MedicalProfessionals
      );
      reset({
        ...carePlan,
        ContingencyAndDisasterPlan: normalizedContingencyPlan,
        Medication: normalizedMedication,
        MedicalProfessionals: normalizedMedicalProfessionals
      });
      setMedicalHistory(carePlan.HealthInformation?.MedicalHistory ?? []);
      setHealthInfoGroups(carePlan.HealthInformation?.Groups ?? []);
      setDislikes(carePlan.MealPreferences?.Dislikes ?? ['']);
      setShortTermGoals(carePlan.Goals?.ShortTerm ?? ['']);
      setLongTermGoals(carePlan.Goals?.LongTerm ?? ['']);
      setDirectedSupportPlans(
        carePlan.DirectedSupportPlan ?? [
          {
            CareNeed: '',
            FrequencyOfServices: '',
            ActionStrategies: '',
            VisitTimeDuration: '',
            ResponsiblePerson: '',
            ByWhen: ''
          }
        ]
      );
      setRegularAppointments(
        carePlan && Array.isArray(carePlan.RegularAppointments)
          ? carePlan.RegularAppointments.map((appt) => ({
              AppointmentType: appt.AppointmentType ?? '',
              Frequency: appt.Frequency ?? '',
              ContactDetails: appt.ContactDetails ?? '',
              Physio: appt.Physio ?? ''
            }))
          : [
              {
                AppointmentType: '',
                Frequency: '',
                ContactDetails: '',
                Physio: ''
              }
            ]
      );
    }
  };

  // Sections for the left panel
  const sections: Section[] = [
    {
      name: 'Personal Info',
      icon: <FaUser />,
      color: theme.palette.primary.main,
      description: 'Basic personal details and contact information'
    },
    {
      name: 'Health Information',
      icon: <FaClinicMedical />,
      color: theme.palette.error.main,
      description: 'Medical conditions and health history'
    },
    {
      name: 'Abilities & Support Needs',
      icon: <FaWheelchair />,
      color: theme.palette.secondary.main,
      description: 'Client capabilities and required support'
    },
    {
      name: 'Meal Preferences',
      icon: <FaUtensils />,
      color: theme.palette.success.main,
      description: 'Dietary preferences and restrictions'
    },
    {
      name: 'Emergency Contacts',
      icon: <FaExclamationTriangle />,
      color: theme.palette.error.dark,
      description: 'Emergency contact information'
    },
    {
      name: 'Goals',
      icon: <FaStar />,
      color: theme.palette.success.main,
      description: 'Individual objectives and aspirations'
    },
    {
      name: 'Medical Professionals',
      icon: <FaHospital />,
      color: theme.palette.info.main,
      description: 'Healthcare providers and specialists'
    },
    {
      name: 'Regular Appointments',
      icon: <FaCalendarAlt />,
      color: theme.palette.primary.dark,
      description: 'Scheduled medical and therapy appointments'
    },
    {
      name: 'Contingency Plan',
      icon: <FaExclamationCircle />,
      color: theme.palette.warning.dark,
      description: 'Emergency response procedures'
    },
    {
      name: 'Directed Support Plan',
      icon: <FaHandsHelping />,
      color: theme.palette.secondary.dark,
      description: 'Support strategies and schedules'
    },
    {
      name: 'Medication',
      icon: <FaPills />,
      color: theme.palette.info.dark,
      description: 'Current medications and dosages'
    }
  ];

  const renderSectionDetails = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            gap: 1
          }}
        >
          <CircularProgress size={50} thickness={4} />
          <Typography variant="subtitle1" color="textSecondary">
            Loading care plan data...
          </Typography>
        </Box>
      );
    }

    if (errorMessage && !carePlan) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            gap: 1,
            textAlign: 'center',
            p: 1
          }}
        >
          <FaExclamationCircle style={{ fontSize: '40px', color: theme.palette.error.main }} />
          <Typography variant="subtitle1" color="error">
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Unable to load care plan data
          </Typography>
        </Box>
      );
    }

    if (!carePlan) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            gap: 1,
            textAlign: 'center',
            p: 1
          }}
        >
          <FaListAlt style={{ fontSize: '40px', color: theme.palette.text.secondary }} />
          <Typography variant="subtitle1" color="textSecondary">
            No care plan data available
          </Typography>
        </Box>
      );
    }

    const sectionProps: ExtendedSectionProps = {
      editMode,
      isMobile,
      handleEditToggle,
      handleSubmit: (data, e) => {
        console.log('handleSubmit called with:', data, e);
        console.log('Form errors before submission:', errors);
        const result = handleSubmit(onSubmit)(e);
        if (Object.keys(errors).length > 0) {
          console.log('Validation errors preventing submission:', errors);
        }
        return result;
      },
      handleCancel,
      control,
      errors,
      getValues,
      setValue,
      carePlan,
      medicalHistory,
      setMedicalHistory,
      dislikes,
      setDislikes,
      shortTermGoals,
      setShortTermGoals,
      longTermGoals,
      setLongTermGoals,
      directedSupportPlans,
      setDirectedSupportPlans,
      healthInfoGroups,
      setHealthInfoGroups,
      regularAppointments,
      setRegularAppointments
    };

    switch (selectedSection) {
      case 'Personal Info':
        return <PersonalInfoSection {...sectionProps} />;
      case 'Health Information':
        return <HealthInfoSection {...sectionProps} />;
      case 'Abilities & Support Needs':
        return <AbilitiesSection {...sectionProps} />;
      case 'Meal Preferences':
        return <MealPreferencesSection {...sectionProps} />;
      case 'Emergency Contacts':
        return <EmergencyContactsSection {...sectionProps} />;
      case 'Goals':
        return <GoalsSection {...sectionProps} />;
      case 'Medical Professionals':
        return <MedicalProfessionalsSection {...sectionProps} />;
      case 'Regular Appointments':
        return <RegularAppointmentsSection {...sectionProps} />;
      case 'Contingency Plan':
        return <ContingencyPlanSection {...sectionProps} />;
      case 'Directed Support Plan':
        return <DirectedSupportPlanSection {...sectionProps} />;
      case 'Medication':
        return <MedicationSection {...sectionProps} />;
      default:
        return (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <FaListAlt style={{ fontSize: '40px' }} />
            <Typography variant="subtitle1" color="textSecondary">
              Select a section to view details
            </Typography>
          </Box>
        );
    }
  };

  const renderNavigationPanel = () => (
    <MainCard sx={{ height: '100%', borderRadius: 1, overflowY: 'auto', p: isMobile ? 1 : 1.5 }}>
      <Typography
        variant={isMobile ? 'subtitle1' : 'h6'}
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          color: theme.palette.primary.main,
          mb: isMobile ? 1 : 1.5
        }}
      >
        <FaListAlt style={{ fontSize: isMobile ? '16px' : '18px' }} /> Care Plan
      </Typography>
      <List disablePadding>
        {sections.map((section) => (
          <Tooltip key={section.name} title={section.description} placement="right" arrow>
            <ListItem
              button
              selected={selectedSection === section.name}
              onClick={() => handleSectionClick(section.name)}
              sx={{
                borderRadius: 0.5,
                mb: isMobile ? 0.25 : 0.5,
                bgcolor:
                  selectedSection === section.name ? theme.palette.action.selected : 'transparent',
                '&:hover': { bgcolor: theme.palette.action.hover },
                transition: 'all 0.2s ease',
                p: isMobile ? 0.5 : 1
              }}
            >
              <ListItemIcon
                sx={{
                  color:
                    selectedSection === section.name ? section.color : theme.palette.text.secondary,
                  minWidth: isMobile ? 28 : 32
                }}
              >
                {React.cloneElement(section.icon, {
                  style: { fontSize: isMobile ? '14px' : '16px' }
                })}
              </ListItemIcon>
              <ListItemText
                primary={section.name}
                primaryTypographyProps={{
                  fontWeight: selectedSection === section.name ? 'bold' : 'medium',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  color: selectedSection === section.name ? section.color : 'inherit'
                }}
              />
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </MainCard>
  );

  return (
    <FormProvider {...methods}>
      {isMobile && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            top: 'auto',
            bottom: 0,
            display: { xs: 'block', sm: 'none' },
            zIndex: theme.zIndex.drawer + 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper
          }}
        >
          <Toolbar sx={{ minHeight: '48px!important', justifyContent: 'space-between', px: 1.5 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
              {selectedSection}
            </Typography>
            <IconButton edge="end" color="inherit" aria-label="menu" onClick={toggleDrawer}>
              <FaBars style={{ fontSize: '18px' }} />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={toggleDrawer}
          sx={{
            '& .MuiDrawer-paper': {
              height: '65vh',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              overflow: 'hidden',
              background: theme.palette.background.default
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="subtitle1">Care Plan Sections</Typography>
            <IconButton onClick={toggleDrawer}>
              <FaTimes style={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
          <Box sx={{ p: 1, overflowY: 'auto' }}>{renderNavigationPanel()}</Box>
        </Drawer>
      )}

      <Grid container spacing={isMobile ? 0 : gridSpacing / 2} sx={{ pb: isMobile ? '48px' : 0 }}>
        <Grid item xs={12} sm={12} md={4} lg={3} sx={{ display: { xs: 'none', sm: 'block' } }}>
          {renderNavigationPanel()}
        </Grid>

        <Grid item xs={12} sm={12} md={8} lg={9}>
          <MainCard sx={{ minHeight: isMobile ? 'auto' : '70vh', borderRadius: isMobile ? 0 : 1 }}>
            <Box sx={{ p: isMobile ? 1 : 1.5 }}>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: theme.palette.primary.dark,
                  mb: 1.5
                }}
              >
                <FaUser style={{ fontSize: isMobile ? '18px' : '20px' }} />
                {carePlan?.PreferredName || 'Client'}'s Care Plan
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {isMobile && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {sections.find((s: Section) => s.name === selectedSection)?.icon}
                  <Typography variant="subtitle1" color="primary">
                    {selectedSection}
                  </Typography>
                </Box>
              )}
              <Box sx={{ pb: isMobile ? 1 : 0 }}>{renderSectionDetails()}</Box>
            </Box>
          </MainCard>
        </Grid>
      </Grid>

      {isMobile && <MobileNavButton toggleDrawer={toggleDrawer} drawerOpen={drawerOpen} />}
    </FormProvider>
  );
};

export default CarePlanComponent;
